<script setup lang="ts">
/**
 * The conversation with a member, as the portal draws it (#244). The Chat page and Create task both render this one
 * component, so the two cannot drift: the kit's thread (ZhChatThread), and in it the pieces every conversation has.
 *
 * - The thinking state: `thinking` ends the thread with the member at work on a reply.
 * - The tool log: `toolLog` names what the member did while writing each reply, drawn under it (ChatToolLog).
 * - The tool calls waiting on you: `calls` are drawn as their cards at the end of the log (ChatToolConfirm), with
 *   `callDecided` and `deciding` saying which are decided and whether a decision is on its way; a choice is emitted
 *   as `decide`.
 * - The composer: with `v-model:draft`, the message box and Send (ChatComposer), `canSend` saying whether Send may
 *   be pressed and `closed` whether the conversation takes messages; a send is emitted as `send`, a paste into it as
 *   `paste`.
 *
 * What happens in the conversation (reading it, sending, deciding) stays with the page that holds it. The page's own
 * additions come through slots: the head's `avatar` and `actions`, `top` under the head, `sender-avatar`; in a
 * message, `message-extra` above the tool log and `message-end` below it; in the log, `log-end` above the waiting
 * calls and `log-after` below them; and `composer-before` above the composer, `composer-notices` inside it above the
 * message box, and `composer-tools` beside Send. A page with no draft can still pass a `composer` of its own.
 *
 * `bounded` draws the kit's bounded-height thread (`zh-chat--bounded`), for a page that scrolls with the document: the
 * thread fills the box its container gives it and scrolls inside it, and the head and the composer stay in view.
 */
import { type ChatEntry, type SurfaceMember, ZhChatThread } from '@zerohuman/ui'
import { computed, ref } from 'vue'
import type { ChatMessageRow, ChatToolCallDecision, GateDecision } from '@/api'
import ChatComposer from '@/components/ChatComposer.vue'
import ChatToolConfirm from '@/components/ChatToolConfirm.vue'
import ChatToolLog from '@/components/ChatToolLog.vue'
import { useUiMode } from '@/composables/useUiMode'
import type { ChatToolItem } from '@/lib/chat'
import { memberLabel } from '@/lib/memberLabel'
import { toolActionLabel } from '@/lib/presentation'

const props = defineProps<{
    member: SurfaceMember & { displayName?: string | null }
    role: string
    entries: readonly ChatEntry[]
    thinking?: boolean
    /** Whether a reply's Details start open: the portal opens them in Advanced. */
    detailsOpen?: boolean
    bounded?: boolean
    /** The tool log under the entry with this id; an entry with none draws no log. */
    toolLog?: (entryId: string) => ChatToolItem[]
    /** The tool calls drawn as cards at the end of the log, oldest first. */
    calls?: readonly ChatMessageRow[]
    /** The decision just made on a call, while its card still shows it. */
    callDecided?: (call: ChatMessageRow) => GateDecision | undefined
    /** A decision on a call is on its way to the API: every card waits on it. */
    deciding?: boolean
    /** The message being written. With it, the conversation draws its composer. */
    draft?: string
    canSend?: boolean
    /** The conversation takes no more messages. */
    closed?: boolean
}>()

const emit = defineEmits<{
    'update:draft': [draft: string]
    send: []
    paste: [event: ClipboardEvent]
    decide: [call: ChatMessageRow, decision: ChatToolCallDecision]
}>()

/**
 * A page's slot, with the scope the kit's thread (or this component, in its place) gives it: the same scopes the kit
 * passes, so a page fills them exactly as it would fill ZhChatThread's.
 */
// biome-ignore lint/suspicious/noExplicitAny: each slot passes on whichever scope the kit's thread gives it
type PageSlot = (scope: any) => unknown
const slots = defineSlots<Record<string, PageSlot | undefined>>()
const { mode } = useUiMode()

/** The slots this component places itself; every other one the page fills passes through to the kit's thread. */
const PLACED = new Set([
    'message-extra',
    'message-end',
    'log-end',
    'log-after',
    'composer',
    'composer-before',
    'composer-notices',
    'composer-tools',
])
/** Read on every draw, as the page's slots can come and go. */
function passed(): string[] {
    return Object.keys(slots).filter(name => !PLACED.has(name))
}

const composing = computed(() => props.draft !== undefined)

function hasComposer(): boolean {
    return composing.value || !!slots.composer || !!slots['composer-before']
}

function hasMessageExtra(): boolean {
    return !!props.toolLog || !!slots['message-extra'] || !!slots['message-end']
}

/** Who asks on a call's card, as the portal labels the member. */
const memberName = computed(() => memberLabel(props.member))

/** A call's tool as the thread names it in the current mode, or null when the call names none. */
function toolLabel(call: ChatMessageRow): string | null {
    return call.toolName ? toolActionLabel(call.toolName, mode.value) : null
}

/** The tool log under one entry, or none. */
function logOf(entry: ChatEntry): ChatToolItem[] {
    return props.toolLog && entry.id ? props.toolLog(entry.id) : []
}

const thread = ref<InstanceType<typeof ZhChatThread> | null>(null)

/** The thread's scrolling log (`.zh-chat__log`) as the kit exposes it, so the page can keep the reader at its end. */
const log = computed((): HTMLElement | null => thread.value?.log ?? null)
defineExpose({ log })
</script>

<template>
	<ZhChatThread
		ref="thread"
		:class="{ 'zh-chat--bounded': bounded }"
		:member="member"
		:role="role"
		:entries="entries"
		:thinking="thinking"
		:details-open="detailsOpen"
	>
		<template v-for="name in passed()" #[name]="scope">
			<slot :name="name" v-bind="scope ?? {}" />
		</template>
		<template v-if="hasMessageExtra()" #message-extra="{ entry, index }">
			<slot name="message-extra" :entry="entry" :index="index" />
			<ChatToolLog v-if="logOf(entry).length" :items="logOf(entry)" />
			<slot name="message-end" :entry="entry" :index="index" />
		</template>
		<template #log-end>
			<slot name="log-end" />
			<ChatToolConfirm
				v-for="call in calls ?? []"
				:key="call.id"
				:call="call"
				:member-name="memberName"
				:tool-label="toolLabel(call)"
				:decided="callDecided?.(call)"
				:busy="deciding"
				@decide="decision => emit('decide', call, decision)"
			/>
			<slot name="log-after" />
		</template>
		<template v-if="hasComposer()" #composer>
			<slot name="composer-before" />
			<ChatComposer
				v-if="composing"
				:draft="draft ?? ''"
				:can-send="canSend ?? false"
				:disabled="closed"
				@update:draft="value => emit('update:draft', value)"
				@send="emit('send')"
				@paste="(event: ClipboardEvent) => emit('paste', event)"
			>
				<slot name="composer-notices" />
				<template #tools><slot name="composer-tools" /></template>
			</ChatComposer>
			<slot v-else name="composer" />
		</template>
	</ZhChatThread>
</template>
